import { Button, Field, Input, Stack, Switch } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { useFormik } from "formik";
import { useStores } from "../../../../shared/stores/RootStore";
import { toast } from "../../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../../shared/views/CustomDialog";

const i18prefix = "map:forecast.saveModal";
const formId = "createOrUpdateHlaForecastScenario";

const CreateOrUpdateHlaForecastScenarioModalBase = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { projectStore, hlaForecastStore } = useStores();

  const selectedProject = projectStore.getSelected();
  const selectedScenario = hlaForecastStore.selectedScenario;

  const formik = useFormik({
    initialValues: {
      name: selectedScenario?.name ?? "",
      overwrite: !!selectedScenario,
    },

    onSubmit: async (values) => {
      const updateRequest = {
        name: values.name,
        actions: hlaForecastStore.actions.map((action) => ({
          attributeChanges: action.attributeChanges,
          filters: action.filters,
        })),
      };

      const promise = values.overwrite
        ? hlaForecastStore.updateScenario(selectedScenario!.id, updateRequest)
        : hlaForecastStore.createScenario({
            ...updateRequest,
            projectId: selectedProject!.id,
          });

      promise
        .then(() => {
          toast("success", t(`${i18prefix}.toast.success`));
        })
        .catch(() => {
          toast("error", t(`${i18prefix}.toast.error`));
        })
        .finally(() => onClose());
    },
  });

  const { name, overwrite } = formik.values;
  const nameIsValid = overwrite || name !== (selectedScenario?.name ?? "");

  return (
    <>
      <CustomDialog
        onClose={onClose}
        header={t(`${i18prefix}.header`)}
        footer={
          <Button
            disabled={!nameIsValid}
            flexShrink={0}
            colorPalette="primary"
            type="submit"
            form={formId}
          >
            {selectedScenario && overwrite
              ? t(`${i18prefix}.submit.update`)
              : t(`${i18prefix}.submit.create`)}
          </Button>
        }
      >
        <form id={formId} onSubmit={formik.handleSubmit}>
          <Stack gap={4}>
            <Field.Root required invalid={!nameIsValid}>
              <Field.Label>{t(`${i18prefix}.name`)}</Field.Label>
              <Input
                name="name"
                onChange={formik.handleChange}
                value={name}
                autoFocus
              />
              <Field.ErrorText>
                {t(`${i18prefix}.error.duplicatedName`)}
              </Field.ErrorText>
            </Field.Root>

            {!!selectedScenario && (
              <Switch.Root
                id="overwrite"
                name="overwrite"
                checked={overwrite}
                onChange={formik.handleChange}
                colorPalette={"primary"}
              >
                <Switch.HiddenInput />
                <Switch.Control />
                <Switch.Label>
                  {t(`${i18prefix}.overwrite`, {
                    name: selectedScenario.name,
                  })}
                </Switch.Label>
              </Switch.Root>
            )}
          </Stack>
        </form>
      </CustomDialog>
    </>
  );
};

export const CreateOrUpdateHlaForecastScenarioModal = observer(
  CreateOrUpdateHlaForecastScenarioModalBase,
);
